// The tenant a page load explicitly ASKED for: `?tenantSlug=<slug>` on the way
// in (a module's switcher handoff, an SSO return, a mailed link, a bookmark).
//
// It needs its own channel because `kb_tenant_slug` cannot carry the intent. That
// key is the workspace's CURRENT scope, written by the tenants store on every
// load and every switcher pick — so by the time the store is choosing an active
// tenant it can no longer tell "the URL just asked for aurora-systems" apart from
// "aurora-systems is what this browser was scoped to last time". It read the
// remembered pick instead and overwrote the request, which is how a deep link
// into a second tenant landed in the first one.
//
// sessionStorage, not localStorage: this is one navigation's intent, in THIS tab.
// It must not follow the browser into other tabs or outlive the tab that was sent
// the link. And it is CONSUMED on read (one-shot), so it governs exactly the page
// load it arrived with — never a later switcher pick, which would otherwise be
// undone by the next reload.
//
// The slug is never trusted on its own: the tenants store only honours it when
// the freshly fetched membership list still contains it, so a link naming a
// tenant the signed-in user is not a member of falls back to the default.
const REQUESTED_TENANT_KEY = 'lumi:requestedTenantSlug';

function sessionStore(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage ?? null;
  } catch {
    return null;
  }
}

/** Record the tenant this navigation asked for. An empty slug clears the request. */
export function writeRequestedTenantSlug(slug: string): void {
  const value = String(slug || '').trim();
  try {
    if (value) sessionStore()?.setItem(REQUESTED_TENANT_KEY, value);
    else sessionStore()?.removeItem(REQUESTED_TENANT_KEY);
  } catch {
    /* storage unavailable — the scope written by the guard still applies */
  }
}

/**
 * The requested tenant slug, or `null`. CONSUMES it: a second caller, and every
 * later load in this tab, sees nothing.
 */
export function takeRequestedTenantSlug(): string | null {
  try {
    const value = sessionStore()?.getItem(REQUESTED_TENANT_KEY) ?? null;
    sessionStore()?.removeItem(REQUESTED_TENANT_KEY);
    return value ? value.trim() || null : null;
  } catch {
    return null;
  }
}
